import { StringContext } from "../StringContext";
import { ScrollController } from "./ScrollController";

/**
 * Default scroll controller using native browser scrolling behavior.
 * Handles `scrollTop`, easing delta over time for smooth lerped animations.
 */
export class StringScrollDefault extends ScrollController {
  /** Unique name identifier for this scroll mode. */
  public readonly name: string = "default";

  /** Last known scroll position for direction detection */
  private previousScrollTop: number = 0;

  /** Tracks whether native scroll is currently active */
  private isScrolling: boolean = false;

  /** Timestamp of the last native scroll event */
  private lastScrollEventTime: number = 0;

  /** Delay before considering scroll as stopped (ms) */
  private readonly scrollStopDelay = 120;

  /**
   * Constructs a new instance of the default scroll controller.
   * @param context Shared string system context.
   */
  constructor(context: StringContext) {
    super(context);
  }

  /**
   * Called every animation frame.
   * Applies easing to scroll delta and updates lerped value.
   * Fires `onScrollStop` once movement has settled.
   */
  public onFrame(): void {
    if (this.context.data.scroll.delta !== 0) {
      const delta = this.context.data.scroll.delta * this.context.data.scroll.speedAccelerate;
      this.context.data.scroll.delta -= delta;
      this.context.data.scroll.lerped = delta;
      // this.onCalcUpdate();

      if (Math.abs(this.context.data.scroll.lerped) < 0.1) {
        this.context.data.scroll.delta = 0;
        this.context.data.scroll.lerped = 0;
        if (this.isScrolling) {
          this.isScrolling = false;
          this.onScrollStop();
          this.clearScrollingClasses();
        }
      }
    }

    if (this.isScrolling) {
      const now = performance.now();
      if (now - this.lastScrollEventTime > this.scrollStopDelay) {
        this.isScrolling = false;
        this.onScrollStop();
        this.clearScrollingClasses();
      }
    }
  }

  /**
   * Called on native scroll event.
   * Syncs the internal scroll state with the current page scroll.
   * @param e Scroll event object (unused).
   */
  public onScroll(e: any): void {
    const scrollTop = this.context.data.scroll.elementContainer.scrollTop;
    this.context.data.scroll.current = scrollTop;
    this.context.data.scroll.target = scrollTop;
    this.context.data.scroll.transformedCurrent = scrollTop;

    if (scrollTop !== this.previousScrollTop) {
      this.updateScrollDirection(scrollTop > this.previousScrollTop);
      this.previousScrollTop = scrollTop;
    }

    this.lastScrollEventTime = performance.now();
    if (!this.isScrolling) {
      this.isScrolling = true;
      this.onScrollStart();
    }
  }

  /**
   * Handles wheel input by updating scroll delta for easing.
   * Triggers `onScrollStart` if delta begins from zero.
   * @param e Wheel event.
   */
  public onWheel(e: any): void {
    if (e.deltaY !== 0) {
      if (this.context.data.scroll.delta === 0 && !this.isScrolling) {
        this.isScrolling = true;
        this.onScrollStart();
      }
      const plusDelta = e.deltaY;
      if (this.context.data.scroll.target === 0) {
        this.context.data.scroll.delta += Math.max(0, e.deltaY);
      }

      this.context.data.scroll.delta += plusDelta;

      this.lastScrollEventTime = performance.now();
    }
  }

  public deactivate(): void {
    super.deactivate();
    this.isScrolling = false;
    this.lastScrollEventTime = 0;
    this.previousScrollTop = this.context.data.scroll.current;
  }

  public scrollTo(position: number, immediate?: boolean) {
    this.context.data.scroll.target = position;
    this.context.data.scroll.current = position;
    this.context.data.scroll.delta = 0;
    this.context.data.scroll.lerped = 0;

    if (this._scrollDirection === "vertical") {
      this.context.data.scroll.scrollContainer?.scrollTo({
        top: position,
        left: 0,
        behavior: immediate ? "auto" : "smooth",
      });
    } else {
      this.context.data.scroll.scrollContainer?.scrollTo({
        left: position,
        top: 0,
        behavior: immediate ? "auto" : "smooth",
      });
    }
  }
}
